'use client'

import { Loader2, Mail, CheckCircle } from 'lucide-react'
import Link from 'next/link'
import { useState } from 'react'

import { Button } from '@client/components/ui/button'
import { Input } from '@client/components/ui/input'
import { Label } from '@client/components/ui/label'
import { useToast } from '@client/hooks/use-toast'
import { R3Client } from '@client/lib/r3-client'

export function ForgotPasswordForm() {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const { toast } = useToast()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setIsLoading(true)

    try {
      await R3Client.forgotPassword(email)
      setIsSuccess(true)
      toast({
        title: 'Check your email',
        description: "If an account exists with this email, we've sent a password reset link.",
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Something went wrong',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (isSuccess) {
    return (
      <div className="text-center space-y-4">
        <div className="mx-auto w-12 h-12 flex items-center justify-center rounded-full bg-green-100">
          <CheckCircle className="h-6 w-6 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold">Check your email</h2>
        <p className="text-muted-foreground">We've sent a password reset link to:</p>
        <p className="font-medium text-lg">{email}</p>
        <div className="pt-4 space-y-2">
          <p className="text-sm text-muted-foreground">
            Didn't receive the email? Check your spam folder or try Google sign-in.
          </p>
          <p className="text-sm text-muted-foreground">
            Still having issues?{' '}
            <button
              onClick={() => {
                setIsSuccess(false)
                setEmail('')
              }}
              className="text-primary underline"
            >
              Try again
            </button>
          </p>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email address</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
            placeholder="you@example.com"
            className="pl-10"
          />
        </div>
        <p className="text-xs text-muted-foreground">Enter the email address associated with your account</p>
      </div>

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : (
          'Send Reset Link'
        )}
      </Button>

      <div className="text-center text-sm">
        Remember your password?{' '}
        <Link href="/auth/signin" className="text-primary underline">
          Sign in
        </Link>
      </div>
    </form>
  )
}
